import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MessageSquare, RefreshCw, Mail, Lock } from "lucide-react";
import { ReguaAutomaticaCard } from "@/components/whatsapp/ReguaAutomaticaCard";

const CORES = {
  TOKEN_LOGIN: "border-info/40 text-info bg-info/5",
  FECHAMENTO: "border-foreground/40 text-foreground",
  LEMBRETE: "border-warning/40 text-warning bg-warning/5",
  VENCIMENTO: "border-info/40 text-info bg-info/5",
  INADIMPLENCIA: "border-destructive/40 text-destructive bg-destructive/5",
  AVISO_PREVIO: "border-warning/40 text-warning bg-warning/5",
  ATRASO_D1: "border-destructive/40 text-destructive bg-destructive/5",
  ATRASO_D5: "border-destructive/40 text-destructive bg-destructive/5",
  ATRASO_D10: "border-destructive/40 text-destructive bg-destructive/5",
};

const STATUS_CORES = {
  ENVIADO: "border-success/40 text-success bg-success/5",
  ENVIADO_MOCK: "border-info/40 text-info bg-info/5",
  AGENDADO: "border-warning/40 text-warning bg-warning/5",
  PROCESSANDO: "border-warning/40 text-warning bg-warning/5",
  FALHA: "border-destructive/40 text-destructive bg-destructive/5",
  SEM_EMAIL: "border-muted-foreground/40 text-muted-foreground",
  SEM_TELEFONE: "border-muted-foreground/40 text-muted-foreground",
};

const STATUS_ICON = {
  ENVIADO: "🟢",
  ENVIADO_MOCK: "🔵",
  AGENDADO: "🟡",
  PROCESSANDO: "🟡",
  FALHA: "🔴",
};

export default function AdminWhatsApp() {
  const [wa, setWa] = useState([]);
  const [emails, setEmails] = useState([]);

  const load = async () => {
    try {
      const [w, e] = await Promise.all([
        api.get("/whatsapp/disparos"),
        api.get("/comunicacoes/emails"),
      ]);
      setWa(w.data);
      setEmails(e.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-8 fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-tag mb-2">Régua de cobrança · WhatsApp + E-mail institucional</div>
          <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl">Comunicações</h1>
        </div>
        <div className="flex gap-2">
          <Button data-testid="btn-refresh-disparos" variant="outline" onClick={load} className="rounded-sm">
            <RefreshCw size={14} className="mr-2" /> Atualizar
          </Button>
        </div>
      </div>

      <ReguaAutomaticaCard onExecutada={load} />

      <Tabs defaultValue="whatsapp">
        <TabsList className="rounded-sm">
          <TabsTrigger value="whatsapp" data-testid="tab-wa">
            <MessageSquare size={12} className="mr-2" /> WhatsApp ({wa.length})
          </TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">
            <Mail size={12} className="mr-2" /> E-mail ({emails.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="whatsapp" className="mt-6">
          <Card className="p-6 rounded-sm border-border">
            {wa.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground text-sm border border-dashed rounded-sm">
                Nenhum disparo WhatsApp ainda.
              </div>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {wa.map((d) => {
                  const isConfidential = d.confidencial || d.tipo === "TOKEN_LOGIN";
                  return (
                    <div key={d.id} data-testid={`disparo-${d.id}`} className="border border-border p-4 rounded-sm">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2 min-w-0 flex-wrap">
                          <Badge variant="outline" className={`text-[10px] uppercase tracking-widest ${CORES[d.tipo] || "border-border"}`}>
                            {d.tipo}
                          </Badge>
                          {isConfidential && (
                            <Badge variant="outline" className="text-[10px] uppercase tracking-widest border-warning/40 text-warning bg-warning/5">
                              <Lock size={9} className="mr-1" /> Confidencial
                            </Badge>
                          )}
                          <span className="text-sm font-semibold break-words basis-full sm:basis-auto">{d.razao_social}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {d.status && (
                            <Badge
                              variant="outline"
                              className={`text-[10px] uppercase tracking-widest ${STATUS_CORES[d.status] || ""}`}
                              title={d.erro || d.status}
                            >
                              {STATUS_ICON[d.status] || ""} {d.status}
                            </Badge>
                          )}
                          <span className="text-xs font-mono text-muted-foreground">{d.telefone}</span>
                        </div>
                      </div>
                      {isConfidential ? (
                        <p className="text-sm mt-3 leading-relaxed italic text-muted-foreground flex items-center gap-2">
                          <Lock size={12} className="text-warning" />
                          Conteúdo ocultado por motivos de segurança — o código OTP é pessoal e sigiloso do lojista.
                        </p>
                      ) : (
                        <p className="text-sm mt-3 leading-relaxed break-anywhere">{d.mensagem}</p>
                      )}
                      <div className="text-[10px] text-muted-foreground font-mono mt-2 flex items-center gap-3">
                        <span>{new Date(d.created_at).toLocaleString("pt-BR")}</span>
                      </div>
                      {d.erro && (
                        <div className="text-xs text-destructive mt-2 border-l-2 border-destructive pl-2 break-anywhere">
                          {d.erro}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="email" className="mt-6">
          <Card className="p-6 rounded-sm border-border">
            {emails.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground text-sm border border-dashed rounded-sm">
                Nenhum e-mail enviado ainda. Rode a régua para disparar comunicações institucionais.
              </div>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {emails.map((e) => (
                  <div key={e.id} data-testid={`email-${e.id}`} className="border border-border p-4 rounded-sm">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        <Badge variant="outline" className={`text-[10px] uppercase tracking-widest ${CORES[e.tipo] || "border-border"}`}>
                          {e.tipo}
                        </Badge>
                        <span className="text-sm font-semibold break-words basis-full sm:basis-auto">{e.razao_social}</span>
                      </div>
                      <Badge variant="outline" className={`text-[10px] uppercase tracking-widest ${STATUS_CORES[e.status] || ""}`}>
                        {e.status}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground font-mono mt-1 break-anywhere">
                      Para: {e.email}
                    </div>
                    <div className="text-sm mt-2 font-semibold break-anywhere">{e.assunto}</div>
                    {e.erro && (
                      <div className="text-xs text-destructive mt-1 border-l-2 border-destructive pl-2">
                        {e.erro}
                      </div>
                    )}
                    <div className="text-[10px] text-muted-foreground font-mono mt-2">
                      {new Date(e.created_at).toLocaleString("pt-BR")}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
